// Normalizes server + guest carts into one UI shape:
//   { items:[{ productId, title, unitPrice, quantity, photoUrl, lineTotal, available,
//              depositPercent, depositRequired, depositAmount, balanceDue, localPickupOnly }],
//     subtotal, count, source, depositTotal, amountDueNow, balanceDue,
//     hasLocalPickupItems, hasShippableItems, requiresFulfillmentChoice, pickupDepositPercent }
// All money is integer cents. hasLocalPickupItems/hasShippableItems/requiresFulfillmentChoice/
// amountDueNow/balanceDue are a PREVIEW — the real split is only final after
// POST /api/checkout/intent (e.g. choosing "pick up everything" can change a
// shippable line's deposit to pickupDepositPercent).

export const EMPTY_CART = {
  items: [],
  subtotal: 0,
  count: 0,
  source: 'guest',
  depositTotal: 0,
  amountDueNow: 0,
  balanceDue: 0,
  hasLocalPickupItems: false,
  hasShippableItems: false,
  requiresFulfillmentChoice: false,
  pickupDepositPercent: 0,
};

// Server cart (GET/POST/PUT/DELETE /api/cart*) — item.product is the product id;
// title/unitPrice/photoUrl/lineTotal/available/deposit*/localPickupOnly are
// provided by the server.
export function normalizeServerCart(data) {
  const items = (data?.items || []).map((i) => ({
    productId: i.product,
    title: i.title,
    unitPrice: i.unitPrice ?? 0,
    quantity: i.quantity ?? 0,
    photoUrl: i.photoUrl || null,
    lineTotal: i.lineTotal ?? (i.unitPrice ?? 0) * (i.quantity ?? 0),
    available: i.available !== false,
    depositPercent: i.depositPercent ?? 0,
    depositRequired: i.depositRequired ?? false,
    depositAmount: i.depositAmount ?? 0,
    balanceDue: i.balanceDue ?? 0,
    localPickupOnly: i.localPickupOnly ?? false,
  }));
  const subtotal =
    data?.subtotal ?? items.filter((i) => i.available).reduce((s, i) => s + i.lineTotal, 0);
  const count = data?.itemCount ?? items.reduce((s, i) => s + i.quantity, 0);
  const depositTotal = data?.depositTotal ?? 0;
  const amountDueNow = data?.amountDueNow ?? subtotal;
  const balanceDue = data?.balanceDue ?? 0;
  const hasLocalPickupItems = data?.hasLocalPickupItems ?? items.some((i) => i.localPickupOnly);
  const hasShippableItems = data?.hasShippableItems ?? items.some((i) => !i.localPickupOnly);
  const requiresFulfillmentChoice =
    data?.requiresFulfillmentChoice ?? (hasLocalPickupItems && hasShippableItems);
  const pickupDepositPercent = data?.pickupDepositPercent ?? 0;
  return {
    items,
    subtotal,
    count,
    source: 'server',
    depositTotal,
    amountDueNow,
    balanceDue,
    hasLocalPickupItems,
    hasShippableItems,
    requiresFulfillmentChoice,
    pickupDepositPercent,
  };
}

// Guest cart (localStorage array of snapshots) — no category data available, so
// deposits/pickup never apply pre-login (server enriches once the cart is synced).
export function normalizeGuestItems(items) {
  const norm = (items || []).map((i) => ({
    productId: i.productId,
    title: i.title,
    unitPrice: i.unitPrice ?? 0,
    quantity: i.quantity ?? 0,
    photoUrl: i.photoUrl || null,
    lineTotal: (i.unitPrice ?? 0) * (i.quantity ?? 0),
    available: true,
    depositPercent: 0,
    depositRequired: false,
    depositAmount: 0,
    balanceDue: 0,
    localPickupOnly: false,
  }));
  const subtotal = norm.reduce((s, i) => s + i.lineTotal, 0);
  const count = norm.reduce((s, i) => s + i.quantity, 0);
  return {
    items: norm,
    subtotal,
    count,
    source: 'guest',
    depositTotal: 0,
    amountDueNow: subtotal,
    balanceDue: 0,
    hasLocalPickupItems: false,
    hasShippableItems: norm.length > 0,
    requiresFulfillmentChoice: false,
    pickupDepositPercent: 0,
  };
}
